import * as React from 'react';
import { DateTime } from 'luxon';
import RuleType from '../rules/types/RuleType';
import { CodeSnippetGroup } from '../administration/models';

export function addNewLinesInCopyBuffer() {
    document.addEventListener('copy', handleCopyBuffer, true);
}

export function removeAddNewLinesInCopyBuffer() {
    document.removeEventListener('copy', handleCopyBuffer, true);
}

function handleCopyBuffer(e: ClipboardEvent) {
    const text = window!.getSelection()!.toString();
    const updatedText = text.replace(/\\n/g, '\n').replace(/\\t/g, '\t');
    e.clipboardData!.setData('text/plain', updatedText);
    e.preventDefault();
}

export function getFriendlyRuleTypeName(t: RuleType) {
    switch (t) {
        case 'RefDataRule':
            return 'Reference data search';
        case 'ElasticSearchMatchRule':
            return 'Words search';
        case 'ElasticSearchMatchPhraseRule':
            return 'Phrase search';
        case 'ElasticSearchQueryRule':
            return 'Query search';
        case 'ElasticSearchRawRule':
            return 'Elastic search';
        case 'ElasticSearchScriptQueryRule':
            return 'Script query';
        case 'ElasticSearchFuzzyQueryRule':
            return 'Fuzzy query';
        case 'ElasticSearchRegexpQueryRule':
            return 'Regexp query';
        case 'ElasticSearchWildcardQueryRule':
            return 'Wildcard query';
        case 'InferenceRule':
            return 'Inference search';
        case 'NamedEntitiesRecognitionRule':
            return 'Entities search';
        case 'SmartIndexRule':
            return 'Smart index';
        default:
            return 'Undefined';
    }
}

export function getFullSnippetPath(groupId: string, codeSnippetGroups: CodeSnippetGroup[], path: string = ''): string {
    const group = getSnippetGroupById(groupId, codeSnippetGroups);
    path = [group ? group.name + '.' : '', path].join('');
    if (group && group.parent) {
        path = getFullSnippetPath(group.parent, codeSnippetGroups, path);
    }
    return path;
}

function getSnippetGroupById(groupId: string, codeSnippetGroups: CodeSnippetGroup[]) {
    return codeSnippetGroups.find(g => g.id === groupId);
}

export const safeStringLocaleCompare = (a: string | undefined, b: string | undefined) => {
    if (!b && a) {
        return 1;
    } else if (b && !a) {
        return -1;
    } else if (!b && !a) {
        return 0;
    } else {
        return b!.localeCompare(a!);
    }
};

export const handleDropdownItemSelection = (e: React.MouseEvent<HTMLElement, MouseEvent>, sel: Selection) => {
    const getTextNode = (node: ChildNode): ChildNode => {
        return node.lastChild === null ? node : getTextNode(node.lastChild);
    };
    const el = e.target as HTMLElement;
    if (el.className !== 'ant-select-item-option-content') {
        return;
    }
    el.focus();
    const range = document.createRange();
    const textNode = getTextNode(el);
    range.setStart(textNode!, 0);
    range.setEnd(textNode!, (textNode as Text).length);
    sel!.removeAllRanges();
    sel!.addRange(range);
};

const DEFAULT_LUXON_DATE_FORMAT = 'yyyy-MM-dd';

const SUPPORTED_MOMENT_TOKENS = [
    'YYYY',
    'YY',
    'MMMM',
    'MMM',
    'MM',
    'M',
    'DD',
    'D',
    'dddd',
    'ddd',
    'HH',
    'H',
    'hh',
    'h',
    'mm',
    'm',
    'ss',
    's',
    'ZZ',
    'Z',
    'A',
    'a'
].sort((a, b) => b.length - a.length);

const MOMENT_TO_LUXON_TOKEN_MAP: Array<[RegExp, string]> = [
    [/YYYY/g, 'yyyy'],
    [/YY/g, 'yy'],
    [/dddd/g, 'EEEE'],
    [/ddd/g, 'EEE'],
    [/DD/g, 'dd'],
    [/D/g, 'd'],
    [/MMMM/g, 'LLLL'],
    [/MMM/g, 'LLL'],
    [/A/g, 'a'],
    [/a/g, 'a']
];

const convertMomentLiteralsToLuxon = (format: string) => {
    return format.replace(/\[([^[\]]*)\]/g, (_, literal: string) => `'${literal.replace(/'/g, "''")}'`);
};

const hasUnsupportedMomentTokens = (format: string) => {
    let index = 0;

    while (index < format.length) {
        const char = format[index];

        if (char === '[') {
            const literalEnd = format.indexOf(']', index + 1);

            if (literalEnd === -1) {
                return true;
            }

            index = literalEnd + 1;
            continue;
        }

        if (!/[A-Za-z]/.test(char)) {
            index += 1;
            continue;
        }

        const remaining = format.slice(index);
        const matchedToken = SUPPORTED_MOMENT_TOKENS.find(token => remaining.startsWith(token));

        if (!matchedToken) {
            return true;
        }

        index += matchedToken.length;
    }

    return false;
};

const convertMomentFormatToLuxon = (format: string) => {
    if (!format) {
        return format;
    }

    if (hasUnsupportedMomentTokens(format)) {
        return DEFAULT_LUXON_DATE_FORMAT;
    }

    const literalAdjustedFormat = convertMomentLiteralsToLuxon(format);

    return MOMENT_TO_LUXON_TOKEN_MAP.reduce((result, [pattern, replacement]) => {
        return result.replace(pattern, replacement);
    }, literalAdjustedFormat);
};
const isValidNativeDate = (value: unknown): value is Date => {
    return value instanceof Date && Number.isFinite(value.getTime());
};

const DATE_CONVERSION_MAX_DEPTH = 2;

const toDateTimeSafeInternal = (input: unknown, depth: number): DateTime | null => {
    if (input === null || input === undefined) {
        return null;
    }

    if (depth > DATE_CONVERSION_MAX_DEPTH) {
        return null;
    }

    if (DateTime.isDateTime(input)) {
        const dateTime = input as DateTime;
        return dateTime.isValid ? dateTime : null;
    }

    if (input instanceof Date) {
        const fromDate = DateTime.fromJSDate(input);
        return fromDate.isValid ? fromDate : null;
    }

    if (typeof input === 'number') {
        const fromMillis = DateTime.fromMillis(input);
        return fromMillis.isValid ? fromMillis : null;
    }

    if (typeof input === 'string') {
        const normalizedInput = input.trim();

        if (!normalizedInput) {
            return null;
        }

        const fromIso = DateTime.fromISO(normalizedInput, { setZone: true });
        if (fromIso.isValid) {
            return fromIso;
        }

        return null;
    }

    if (typeof input === 'object' && input) {
        const dateLike = input as { toDate?: () => unknown; toJSDate?: () => unknown; valueOf?: () => unknown };

        if (typeof dateLike.toDate === 'function') {
            const fromToDate = dateLike.toDate();

            if (fromToDate !== input) {
                const parsed = toDateTimeSafeInternal(fromToDate, depth + 1);

                if (parsed) {
                    return parsed;
                }
            }
        }

        if (typeof dateLike.toJSDate === 'function') {
            const fromToJSDate = dateLike.toJSDate();

            if (fromToJSDate !== input) {
                const parsed = toDateTimeSafeInternal(fromToJSDate, depth + 1);

                if (parsed) {
                    return parsed;
                }
            }
        }

        if (typeof dateLike.valueOf === 'function') {
            const millis = dateLike.valueOf();

            if (typeof millis === 'number' && Number.isFinite(millis)) {
                const fromValueOf = DateTime.fromMillis(millis);
                return fromValueOf.isValid ? fromValueOf : null;
            }
        }
    }

    return null;
};

export const toDateTimeSafe = (input: unknown): DateTime | null => {
    return toDateTimeSafeInternal(input, 0);
};

export const toNativeDateSafe = (input: unknown): Date | null => {
    const dateTime = toDateTimeSafe(input);

    if (!dateTime) {
        return null;
    }

    const nativeDate = dateTime.toJSDate();
    return isValidNativeDate(nativeDate) ? nativeDate : null;
};

export const getFormattedDateRange = (dateRange: [unknown, unknown], dateFormat: string) => {
    const luxonFormat = convertMomentFormatToLuxon(dateFormat);
    const start = toDateTimeSafe(dateRange[0]);
    const end = toDateTimeSafe(dateRange[1]);

    return [start?.toFormat(luxonFormat) ?? '', end?.toFormat(luxonFormat) ?? ''];
};

// eslint-disable-next-line max-len
export const EMAIL_REGEX_PATTERN =
    /[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?/g;

export const validateFileName = (_: unknown, value: string, nameLabel: string = 'Name'): Promise<void> => {
    const invalidChars = /[\\/:*?"<>|]/;

    if (value && invalidChars.test(value)) {
        return Promise.reject(new Error(`${nameLabel} contains invalid characters: \\ / : * ? " < > |`));
    }

    return Promise.resolve();
};

/**
 * Formats a date-like input into a short relative time string prefixed with "Last update:".
 * Example outputs: "Last update: 5m ago", "Last update: 2h ago", "Last update: 3d ago".
 */
export function formatLastUpdateRelative(input?: unknown): string | null {
    if (input === null || input === undefined) {
        return null;
    }

    const dateTimeInput = toDateTimeSafe(input);

    if (!dateTimeInput?.isValid) {
        return null;
    }

    const now = DateTime.now();
    const seconds = Math.max(0, Math.floor(now.diff(dateTimeInput, 'seconds').seconds));

    const minute = 60;
    const hour = 60 * minute;
    const day = 24 * hour;
    const month = 30 * day;
    const year = 365 * day;

    let value: number;
    let unit: string;

    if (seconds < minute) {
        value = seconds;
        unit = 's';
    } else if (seconds < hour) {
        value = Math.floor(seconds / minute);
        unit = 'm';
    } else if (seconds < day) {
        value = Math.floor(seconds / hour);
        unit = 'h';
    } else if (seconds < month) {
        value = Math.floor(seconds / day);
        unit = 'd';
    } else if (seconds < year) {
        value = Math.floor(seconds / month);
        unit = 'mo';
    } else {
        value = Math.floor(seconds / year);
        unit = 'y';
    }

    return `Last update: ${value}${unit} ago`;
}
